import React, { useState, memo, Component } from 'react';
import { XIcon, CheckIcon, DownloadIcon } from 'lucide-react';
import { StoryBox } from './StoryBox';
import { LightbulbEmoji, CheckEmoji, ArgentinaEmoji, FrownEmoji, TargetEmoji, BoxEmoji, BreadEmoji, ShoppingCartEmoji, BowlEmoji, CalendarEmoji, PaletteEmoji, MoneyBagEmoji, SparklesEmoji, PartyEmoji, BriefcaseEmoji, WineEmoji, FamilyEmoji, RocketEmoji, StarEmoji, LightningEmoji, GiftEmoji } from './Emoji';
import { trackPurchaseClick } from './MetaPixel';
import { handleCheckout } from './CheckoutUtils';
export function Problem() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const nextSlide = () => {
    setCurrentSlide(prev => prev === 3 ? 0 : prev + 1);
  };
  const prevSlide = () => {
    setCurrentSlide(prev => prev === 0 ? 3 : prev - 1);
  };
  return <section className="pt-0 pb-16 bg-white" data-id="element-460">
      <div className="container mx-auto px-4" data-id="element-461">
        <div className="max-w-3xl mx-auto" data-id="element-462">
          {/* New ventaja secreta box */}
          <div className="bg-amber-50 rounded-xl p-8 border border-amber-100 mb-12" data-id="element-463">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-amber-700 mb-6" data-id="element-464">
              <span className="mr-2 inline-block" data-id="element-465">
                <LightbulbEmoji size={28} data-id="element-466" />
              </span>
              Tu ventaja secreta empieza ahora
            </h2>
            <p className="text-center text-gray-700 mb-8" data-id="element-467">
              Este no es un recetario. Es un sistema completo para crear picadas
              inolvidables con bajo presupuesto y presentación profesional.
            </p>
            <div className="space-y-4" data-id="element-468">
              {['Qué ingredientes elegir', 'Cómo combinarlos para impactar', 'Qué sabores sorprenden', 'Técnicas de armado paso a paso', 'Estilo visual para redes y reuniones'].map((benefit, index) => <div key={index} className="flex items-start gap-3 p-4 bg-green-50 rounded-lg" data-id="element-469">
                  <div className="bg-green-100 p-1 rounded-full" data-id="element-470">
                    <CheckEmoji size={18} data-id="element-471" />
                  </div>
                  <p className="text-gray-700" data-id="element-472">{benefit}</p>
                </div>)}
            </div>
          </div>
          <div className="bg-amber-50 rounded-xl p-8 border border-amber-100 mb-12" data-id="element-473">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-amber-800 mb-4" data-id="element-474">
              <ArgentinaEmoji size={28} className="inline-block mr-2" data-id="element-475" /> La
              Historia Detrás de Picadas Fáciles
            </h2>
            <p className="text-xl text-center text-amber-700 font-medium mb-6" data-id="element-476">
              20 Años Armando Momentos Inolvidables... Sin Romper el Bolsillo
            </p>
            <div className="flex justify-center mb-6" data-id="element-477">
              <div className="w-64 h-64 rounded-full overflow-hidden border-4 border-amber-200" data-id="element-478">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/8EnxotxioaSth6tz2VCKEL/mesa_lista.png" alt="Familia preparando picada" className="w-full h-full object-cover" data-id="element-479" />
              </div>
            </div>
            <blockquote className="italic text-gray-700 text-center mb-6" data-id="element-480">
              "En Argentina, la picada no es solo comida. Es excusa. Es
              conexión. Es memoria."
            </blockquote>
            <p className="text-gray-700 mb-4" data-id="element-481">
              Durante más de dos décadas, ayudé a familias, emprendedoras y
              anfitrionas a crear encuentros que la gente recordara.
            </p>
            <p className="text-gray-700 mb-4" data-id="element-482">Pero siempre veía lo mismo:</p>
            <ul className="space-y-2 mb-6" data-id="element-483">
              <li className="flex items-start gap-2" data-id="element-484">
                <span className="text-amber-600" data-id="element-485">
                  <FrownEmoji size={20} data-id="element-486" />
                </span>
                <span className="text-gray-700" data-id="element-487">
                  Tablas mal armadas que costaban una fortuna.
                </span>
              </li>
              <li className="flex items-start gap-2" data-id="element-488">
                <span className="text-amber-600" data-id="element-489">
                  <FrownEmoji size={20} data-id="element-490" />
                </span>
                <span className="text-gray-700" data-id="element-491">
                  Fiambres caros sin gracia ni presentación.
                </span>
              </li>
              <li className="flex items-start gap-2" data-id="element-492">
                <span className="text-amber-600" data-id="element-493">
                  <FrownEmoji size={20} data-id="element-494" />
                </span>
                <span className="text-gray-700" data-id="element-495">
                  Ideas repetidas, aburridas, sin alma.
                </span>
              </li>
            </ul>
            <p className="text-gray-700 mb-4" data-id="element-496">
              Hasta que un día me di cuenta… El problema no era la falta de
              ganas. Era la falta de método.
            </p>
            <p className="text-gray-700 font-medium mb-4" data-id="element-497">
              Así nació Picadas Fáciles™: Un sistema probado que enseña cómo
              convertir ingredientes comunes en picadas que parecen premium.
            </p>
            <ul className="space-y-2 mb-6" data-id="element-498">
              <li className="flex items-start gap-2" data-id="element-499">
                <span className="text-amber-600" data-id="element-500">
                  <LightbulbEmoji size={20} data-id="element-501" />
                </span>
                <span className="text-gray-700" data-id="element-502">
                  Aplicando todo lo que aprendí enseñando a cientos de alumnas,
                </span>
              </li>
              <li className="flex items-start gap-2" data-id="element-503">
                <span className="text-amber-600" data-id="element-504">
                  <LightbulbEmoji size={20} data-id="element-505" />
                </span>
                <span className="text-gray-700" data-id="element-506">
                  Y usando lo mismo que yo aplicaba cuando me pagaban por armar
                  picadas para eventos de verdad.
                </span>
              </li>
            </ul>
            <p className="text-gray-700 mb-4" data-id="element-507">
              Hoy, más de 3.000 personas ya descargaron este pack. Y muchas no
              solo lo usan para sorprender en casa… También están vendiendo. Y
              les va cada vez mejor.
            </p>
            <p className="text-gray-700 mb-6" data-id="element-508">
              Porque cuando sabés cómo armar la picada perfecta, no estás
              cocinando. Estás creando una experiencia.
            </p>
            <p className="text-center font-bold text-amber-800 mb-2" data-id="element-509">
              Picadas Fáciles™
            </p>
            <p className="text-center text-gray-700 italic mb-8" data-id="element-510">
              Hecho por una argentina para las argentinas que quieren más: más
              sabor, más estilo, más oportunidades.
            </p>
            <div className="flex justify-center" data-id="element-511">
              <a href="#" className="checkout-fresh bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 px-8 rounded-full shadow-md mx-auto" onClick={handleCheckout} data-id="element-512">
                ¡Quiero Aprender! - AR$17.990
              </a>
            </div>
          </div>
          {/* Statistics section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12" data-id="element-513">
            <div className="bg-amber-50 border border-amber-100 rounded-lg p-6 flex flex-col items-center justify-center" data-id="element-514">
              <div className="text-amber-600 mb-2" data-id="element-515">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-516">
                  <path d="M12 10c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2 2 2z" data-id="element-517" />
                  <path d="M12 2C7.6 2 4 5.6 4 10c0 5 7 12 8 12s8-7 8-12c0-4.4-3.6-8-8-8z" data-id="element-518" />
                </svg>
              </div>
              <p className="text-amber-700 font-medium text-center" data-id="element-519">
                Punta del Este
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-100 rounded-lg p-6 flex flex-col items-center justify-center" data-id="element-520">
              <div className="text-amber-600 mb-2" data-id="element-521">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-522">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" data-id="element-523" />
                  <circle cx="9" cy="7" r="4" data-id="element-524" />
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87" data-id="element-525" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" data-id="element-526" />
                </svg>
              </div>
              <p className="text-amber-700 font-medium text-center" data-id="element-527">
                1,200+ Alumnos
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-100 rounded-lg p-6 flex flex-col items-center justify-center" data-id="element-528">
              <div className="text-amber-600 mb-2" data-id="element-529">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-530">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" data-id="element-531" />
                  <polyline points="22 4 12 14.01 9 11.01" data-id="element-532" />
                </svg>
              </div>
              <p className="text-amber-700 font-medium text-center" data-id="element-533">
                Método Comprobado
              </p>
            </div>
          </div>
          {/* New achievements section */}
          <div className="bg-amber-50 rounded-xl p-8 border border-amber-100 mb-12" data-id="element-534">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-amber-800 mb-6" data-id="element-535">
              <TargetEmoji size={28} className="inline-block mr-2" data-id="element-536" /> Lo Que Vas
              a Lograr con Picadas Fáciles
            </h2>
            <div className="space-y-6" data-id="element-537">
              <div className="flex items-start gap-3" data-id="element-538">
                <div className="text-amber-600 mt-1 flex-shrink-0" data-id="element-539">
                  <CheckEmoji size={20} data-id="element-540" />
                </div>
                <div data-id="element-541">
                  <p className="font-medium text-amber-800" data-id="element-542">
                    Armar picadas espectaculares en menos de 15 minutos
                  </p>
                  <p className="text-gray-700" data-id="element-543">
                    Sin estrés, sin complicaciones, y con lo que tenés en casa.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-544">
                <div className="text-amber-600 mt-1 flex-shrink-0" data-id="element-545">
                  <CheckEmoji size={20} data-id="element-546" />
                </div>
                <div data-id="element-547">
                  <p className="font-medium text-amber-800" data-id="element-548">
                    Elegir ingredientes económicos que parecen gourmet
                  </p>
                  <p className="text-gray-700" data-id="element-549">
                    Y combinarlos de forma estratégica para que rindan más,
                    gusten más, y luzcan mejor.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-550">
                <div className="text-amber-600 mt-1 flex-shrink-0" data-id="element-551">
                  <CheckEmoji size={20} data-id="element-552" />
                </div>
                <div data-id="element-553">
                  <p className="font-medium text-amber-800" data-id="element-554">
                    Lograr una presentación profesional sin vajilla cara
                  </p>
                  <p className="text-gray-700" data-id="element-555">
                    Vas a aprender trucos visuales que hacen que tu tabla se vea
                    premium en fotos y en vivo.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-556">
                <div className="text-amber-600 mt-1 flex-shrink-0" data-id="element-557">
                  <CheckEmoji size={20} data-id="element-558" />
                </div>
                <div data-id="element-559">
                  <p className="font-medium text-amber-800" data-id="element-560">
                    Saber exactamente qué comprar y cuánto
                  </p>
                  <p className="text-gray-700" data-id="element-561">
                    Gracias al checklist inteligente y las recetas fraccionadas
                    por persona. Nada de sobras, nada de gasto de más.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-562">
                <div className="text-amber-600 mt-1 flex-shrink-0" data-id="element-563">
                  <CheckEmoji size={20} data-id="element-564" />
                </div>
                <div data-id="element-565">
                  <p className="font-medium text-amber-800" data-id="element-566">
                    Tener ideas para cada ocasión: partidos, brunch, citas,
                    reuniones familiares o venta
                  </p>
                  <p className="text-gray-700" data-id="element-567">
                    Con el calendario de 30 días, nunca más vas a decir "no sé
                    qué hacer".
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-568">
                <div className="text-amber-600 mt-1 flex-shrink-0" data-id="element-569">
                  <CheckEmoji size={20} data-id="element-570" />
                </div>
                <div data-id="element-571">
                  <p className="font-medium text-amber-800" data-id="element-572">
                    Transformar tus picadas en ingresos desde casa
                  </p>
                  <p className="text-gray-700" data-id="element-573">
                    Podés cobrar por lo que hacés. Crear un menú. Ofrecerlo por
                    WhatsApp. Y empezar a generar dinero con algo que amás.
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-8 flex justify-center" data-id="element-574">
              <a href="#" className="checkout-fresh bg-amber-600 hover:bg-amber-700 text-white font-bold py-4 px-8 rounded-lg shadow-lg transition-all transform hover:scale-105" onClick={handleCheckout} data-id="element-575">
                👉 COMPRAR AHORA - AR$17.990
              </a>
            </div>
          </div>
          {/* "Lo Que Incluye" section */}
          <div className="bg-amber-50 rounded-xl p-8 border border-amber-100 mb-12" data-id="element-576">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-amber-800 mb-4" data-id="element-577">
              <BoxEmoji size={28} className="inline-block mr-2" data-id="element-578" /> Lo Que
              Incluye Picadas Fáciles™
            </h2>
            <p className="text-center text-gray-700 mb-8" data-id="element-579">
              Todo lo que necesitás para dominar el arte de la picada argentina
              — sin gastar de más y con resultados que parecen profesionales.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-580">
              {/* Item 1 */}
              <div className="bg-white rounded-lg p-6 border border-amber-100 shadow-sm" data-id="element-581">
                <div className="flex justify-center mb-3" data-id="element-582">
                  <BreadEmoji size={36} data-id="element-583" />
                </div>
                <h3 className="text-lg font-bold text-amber-800 text-center mb-2" data-id="element-584">
                  Guía Completa de Picadas Fáciles
                </h3>
                <p className="text-gray-600 text-center text-sm" data-id="element-585">
                  +80 ideas económicas · Recetas express listas en minutos ·
                  Tips de armado pro
                </p>
              </div>
              {/* Item 2 */}
              <div className="bg-white rounded-lg p-6 border border-amber-100 shadow-sm" data-id="element-586">
                <div className="flex justify-center mb-3" data-id="element-587">
                  <ShoppingCartEmoji size={36} data-id="element-588" />
                </div>
                <h3 className="text-lg font-bold text-amber-800 text-center mb-2" data-id="element-589">
                  Checklist de Compras Inteligente
                </h3>
                <p className="text-gray-600 text-center text-sm" data-id="element-590">
                  Cantidades exactas por persona · Alternativas económicas ·
                  Combos de súper para gastar menos
                </p>
              </div>
              {/* Item 3 */}
              <div className="bg-amber-50 rounded-lg p-6 border border-amber-100 shadow-sm" data-id="element-591">
                <div className="flex justify-center mb-3" data-id="element-592">
                  <BowlEmoji size={36} data-id="element-593" />
                </div>
                <h3 className="text-lg font-bold text-amber-800 text-center mb-2" data-id="element-594">
                  Recetario Premium de Dips & Untables
                </h3>
                <p className="text-gray-600 text-center text-sm" data-id="element-595">
                  Desde hummus clásico hasta salsas secretas que elevan
                  cualquier tabla
                </p>
              </div>
              {/* Item 4 */}
              <div className="bg-white rounded-lg p-6 border border-amber-100 shadow-sm" data-id="element-596">
                <div className="flex justify-center mb-3" data-id="element-597">
                  <CalendarEmoji size={36} data-id="element-598" />
                </div>
                <h3 className="text-lg font-bold text-amber-800 text-center mb-2" data-id="element-599">
                  Calendario de Picadas (30 días)
                </h3>
                <p className="text-gray-600 text-center text-sm" data-id="element-600">
                  Ideas para cada ocasión: fútbol, after office, familiar,
                  gourmet, express
                </p>
              </div>
              {/* Item 5 */}
              <div className="bg-white rounded-lg p-6 border border-amber-100 shadow-sm" data-id="element-601">
                <div className="flex justify-center mb-3" data-id="element-602">
                  <PaletteEmoji size={36} data-id="element-603" />
                </div>
                <h3 className="text-lg font-bold text-amber-800 text-center mb-2" data-id="element-604">
                  Guía de Presentación y Estilo Visual
                </h3>
                <p className="text-gray-600 text-center text-sm" data-id="element-605">
                  Trucos para que cualquier tabla luzca premium en vivo y en
                  fotos (ideal si querés vender)
                </p>
              </div>
              {/* Item 6 */}
              <div className="bg-white rounded-lg p-6 border border-amber-100 shadow-sm" data-id="element-606">
                <div className="flex justify-center mb-3" data-id="element-607">
                  <MoneyBagEmoji size={36} data-id="element-608" />
                </div>
                <h3 className="text-lg font-bold text-amber-800 text-center mb-2" data-id="element-609">
                  Mini Curso Express: Cómo Vender Picadas Desde Casa
                </h3>
                <p className="text-gray-600 text-center text-sm" data-id="element-610">
                  Paso a paso para calcular costos, armar menú y empezar a
                  generar ingresos reales
                </p>
              </div>
            </div>
            <div className="flex justify-center" data-id="element-611">
              <a href="#" className="checkout-fresh bg-amber-600 hover:bg-amber-700 text-white font-bold py-4 px-8 rounded-lg shadow-lg transition-all transform hover:scale-105 flex items-center justify-center gap-3" onClick={handleCheckout} data-id="element-612">
                <DownloadIcon size={24} data-id="element-613" />
                <span className="text-lg uppercase" data-id="element-614">DESCARGAR - AR$17.990</span>
              </a>
            </div>
          </div>
          {/* New "Ideal Para" section */}
          <div className="bg-amber-50 rounded-xl p-8 border border-amber-100 mb-12" data-id="element-615">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-amber-800 mb-4" data-id="element-616">
              <SparklesEmoji size={28} className="inline-block mr-2" data-id="element-617" /> Ideal
              Para…
            </h2>
            <p className="text-center text-gray-700 mb-8" data-id="element-618">
              ¿Quién Puede Beneficiarse del Sistema Picadas Fáciles™?
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-619">
              {/* User Type 1 */}
              <div className="flex flex-col items-center" data-id="element-620">
                <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mb-4" data-id="element-621">
                  <PartyEmoji size={36} data-id="element-622" />
                </div>
                <h3 className="text-lg font-bold text-amber-800 text-center mb-2" data-id="element-623">
                  Celebraciones Especiales
                </h3>
                <p className="text-gray-700 text-center" data-id="element-624">
                  Cumpleaños, reuniones familiares, asados, previas o partidos
                  de fútbol. Siempre vas a tener una idea lista para sorprender
                  sin gastar de más.
                </p>
              </div>
              {/* User Type 2 */}
              <div className="flex flex-col items-center" data-id="element-625">
                <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mb-4" data-id="element-626">
                  <BriefcaseEmoji size={36} data-id="element-627" />
                </div>
                <h3 className="text-lg font-bold text-amber-800 text-center mb-2" data-id="element-628">
                  Emprendedores Gastronómicos
                </h3>
                <p className="text-gray-700 text-center" data-id="element-629">
                  Personas que quieren vender picadas caseras con calidad
                  profesional, con un método probado que te enseña a calcular
                  costos, armar menús y ganar clientes.
                </p>
              </div>
              {/* User Type 3 */}
              <div className="flex flex-col items-center" data-id="element-630">
                <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mb-4" data-id="element-631">
                  <WineEmoji size={36} data-id="element-632" />
                </div>
                <h3 className="text-lg font-bold text-amber-800 text-center mb-2" data-id="element-633">
                  Amantes de la Buena Mesa
                </h3>
                <p className="text-gray-700 text-center" data-id="element-634">
                  Si te apasiona la comida y buscás elevar tu nivel con
                  presentaciones de revista, acá vas a encontrar trucos simples
                  que marcan toda la diferencia.
                </p>
              </div>
              {/* User Type 4 */}
              <div className="flex flex-col items-center" data-id="element-635">
                <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mb-4" data-id="element-636">
                  <FamilyEmoji size={36} data-id="element-637" />
                </div>
                <h3 className="text-lg font-bold text-amber-800 text-center mb-2" data-id="element-638">
                  Anfitrionas y Familias
                </h3>
                <p className="text-gray-700 text-center" data-id="element-639">
                  Quienes quieren disfrutar de momentos inolvidables en casa,
                  con picadas fáciles, rápidas y económicas que todos elogian.
                </p>
              </div>
            </div>
          </div>
          {/* New "Resultados Comprobados" section */}
          <div className="bg-amber-50 rounded-xl p-8 border border-amber-100 mb-12" data-id="element-640">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-amber-800 mb-4" data-id="element-641">
              <RocketEmoji size={28} className="inline-block mr-2" data-id="element-642" /> Resultados
              Comprobados
            </h2>
            <p className="text-center text-gray-700 mb-8" data-id="element-643">
              Miles de personas ya transformaron la forma de armar sus picadas
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10" data-id="element-644">
              {/* Feature 1 */}
              <div className="bg-white rounded-lg p-6 border border-amber-100 shadow-sm" data-id="element-645">
                <div className="flex justify-center mb-4" data-id="element-646">
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center" data-id="element-647">
                    <span className="text-amber-600 text-2xl" data-id="element-648">🥖</span>
                  </div>
                </div>
                <h3 className="text-lg font-bold text-amber-800 text-center mb-2" data-id="element-649">
                  Técnicas Profesionales
                </h3>
                <p className="text-gray-700 text-center text-sm" data-id="element-650">
                  Basadas en principios de presentación y combinación usados en
                  eventos y catering reales en Argentina.
                </p>
              </div>
              {/* Feature 2 */}
              <div className="bg-white rounded-lg p-6 border border-amber-100 shadow-sm" data-id="element-651">
                <div className="flex justify-center mb-4" data-id="element-652">
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center" data-id="element-653">
                    <span className="text-amber-600 text-2xl" data-id="element-654">📚</span>
                  </div>
                </div>
                <h3 className="text-lg font-bold text-amber-800 text-center mb-2" data-id="element-655">
                  Experiencia Validada
                </h3>
                <p className="text-gray-700 text-center text-sm" data-id="element-656">
                  Recetas y métodos probados en reuniones, talleres y en más de
                  3.000 descargas del sistema Picadas Fáciles™.
                </p>
              </div>
              {/* Feature 3 */}
              <div className="bg-white rounded-lg p-6 border border-amber-100 shadow-sm" data-id="element-657">
                <div className="flex justify-center mb-4" data-id="element-658">
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center" data-id="element-659">
                    <span className="text-amber-600 text-2xl" data-id="element-660">🤝</span>
                  </div>
                </div>
                <h3 className="text-lg font-bold text-amber-800 text-center mb-2" data-id="element-661">
                  Comunidad de Apoyo
                </h3>
                <p className="text-gray-700 text-center text-sm" data-id="element-662">
                  Unite a cientos de anfitrionas, familias y emprendedores que
                  ya dominan el arte de la picada irresistible.
                </p>
              </div>
            </div>
            <h3 className="text-xl font-bold text-center text-amber-800 mb-6" data-id="element-663">
              📊 Números que Hablan
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-id="element-664">
              {/* Stat 1 */}
              <div className="bg-white rounded-lg p-5 border border-amber-100 shadow-sm text-center" data-id="element-665">
                <div className="text-amber-600 font-bold text-3xl mb-2" data-id="element-666">
                  3.000+
                </div>
                <div className="text-amber-800 font-medium" data-id="element-667">Descargas</div>
                <p className="text-gray-600 text-sm mt-1" data-id="element-668">
                  Clientes satisfechos aplicando el sistema en todo el país.
                </p>
              </div>
              {/* Stat 2 */}
              <div className="bg-white rounded-lg p-5 border border-amber-100 shadow-sm text-center" data-id="element-669">
                <div className="text-amber-600 font-bold text-3xl mb-2" data-id="element-670">
                  20+
                </div>
                <div className="text-amber-800 font-medium" data-id="element-671">
                  Años de experiencia
                </div>
                <p className="text-gray-600 text-sm mt-1" data-id="element-672">
                  En el mundo de la cocina práctica, económica y visualmente
                  impactante.
                </p>
              </div>
              {/* Stat 3 */}
              <div className="bg-white rounded-lg p-5 border border-amber-100 shadow-sm text-center" data-id="element-673">
                <div className="text-amber-600 font-bold text-3xl mb-2" data-id="element-674">
                  100%
                </div>
                <div className="text-amber-800 font-medium" data-id="element-675">
                  Método Comprobado
                </div>
                <p className="text-gray-600 text-sm mt-1" data-id="element-676">
                  Transforma ingredientes simples en experiencias gourmet que
                  sorprenden.
                </p>
              </div>
            </div>
            <p className="text-gray-700 text-center mb-8" data-id="element-677">
              Ya son miles los que pasaron de armar picadas improvisadas a crear
              tablas dignas de restaurante. Muchos incluso comenzaron a vender
              picadas premium desde casa, recuperando la inversión en la primera
              semana.
            </p>
            <div className="flex justify-center" data-id="element-678">
              <a href="#" className="checkout-fresh bg-amber-600 hover:bg-amber-700 text-white font-bold py-4 px-8 rounded-lg shadow-lg transition-all transform hover:scale-105" onClick={handleCheckout} data-id="element-679">
                👉 QUIERO VENDER PICADAS
              </a>
            </div>
          </div>
          {/* New "Testimonios" section */}
          <div id="testimonios" className="bg-amber-50 rounded-xl p-8 border border-amber-100 mb-12" data-id="element-680">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-amber-800 mb-6" data-id="element-681">
              <StarEmoji size={28} className="inline-block mr-2" data-id="element-682" /> Testimonios
              de Clientes Reales
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-683">
              {/* Testimonio 1 */}
              <div className="bg-white rounded-lg p-6 border border-amber-100 shadow-sm" data-id="element-684">
                <div className="flex items-center mb-3" data-id="element-685">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mr-3" data-id="element-686">
                    <span className="text-amber-600 text-lg" data-id="element-687">L</span>
                  </div>
                  <div data-id="element-688">
                    <h3 className="font-bold text-amber-800" data-id="element-689">Lucía, 32 años</h3>
                    <p className="text-gray-600 text-sm" data-id="element-690">Buenos Aires</p>
                  </div>
                  <div className="ml-auto" data-id="element-691">
                    <div className="flex text-amber-500" data-id="element-692">
                      <span data-id="element-693">★★★★★</span>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 italic" data-id="element-694">
                  "Siempre gastaba de más y nunca quedaba como quería. Con
                  Picadas Fáciles aprendí a comprar lo justo y a presentarlo
                  como si fuera gourmet. Mi familia todavía no entiende cómo con
                  tan poco queda tan espectacular."
                </p>
              </div>
              {/* Testimonio 2 */}
              <div className="bg-white rounded-lg p-6 border border-amber-100 shadow-sm" data-id="element-695">
                <div className="flex items-center mb-3" data-id="element-696">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mr-3" data-id="element-697">
                    <span className="text-amber-600 text-lg" data-id="element-698">M</span>
                  </div>
                  <div data-id="element-699">
                    <h3 className="font-bold text-amber-800" data-id="element-700">
                      Mariana, 41 años
                    </h3>
                    <p className="text-gray-600 text-sm" data-id="element-701">Rosario</p>
                  </div>
                  <div className="ml-auto" data-id="element-702">
                    <div className="flex text-amber-500" data-id="element-703">
                      <span data-id="element-704">★★★★★</span>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 italic" data-id="element-705">
                  "Arranqué armando picadas para mis amigas y hoy ya las vendo
                  por Instagram. Lo que pagué por el pack lo recuperé en la
                  primera semana. Literalmente este método me abrió una nueva
                  fuente de ingresos."
                </p>
              </div>
              {/* Testimonio 3 */}
              <div className="bg-white rounded-lg p-6 border border-amber-100 shadow-sm" data-id="element-706">
                <div className="flex items-center mb-3" data-id="element-707">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mr-3" data-id="element-708">
                    <span className="text-amber-600 text-lg" data-id="element-709">D</span>
                  </div>
                  <div data-id="element-710">
                    <h3 className="font-bold text-amber-800" data-id="element-711">Diego, 28 años</h3>
                    <p className="text-gray-600 text-sm" data-id="element-712">Córdoba</p>
                  </div>
                  <div className="ml-auto" data-id="element-713">
                    <div className="flex text-amber-500" data-id="element-714">
                      <span data-id="element-715">★★★★★</span>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 italic" data-id="element-716">
                  "No soy de cocinar, pero quería sorprender a mi novia. Con las
                  recetas express armé una tabla en 15 minutos. Ella pensó que
                  la había comprado. El calendario de picadas me salvó varias
                  veces."
                </p>
              </div>
              {/* Testimonio 4 */}
              <div className="bg-white rounded-lg p-6 border border-amber-100 shadow-sm" data-id="element-717">
                <div className="flex items-center mb-3" data-id="element-718">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mr-3" data-id="element-719">
                    <span className="text-amber-600 text-lg" data-id="element-720">C</span>
                  </div>
                  <div data-id="element-721">
                    <h3 className="font-bold text-amber-800" data-id="element-722">Carla, 37 años</h3>
                    <p className="text-gray-600 text-sm" data-id="element-723">Mendoza</p>
                  </div>
                  <div className="ml-auto" data-id="element-724">
                    <div className="flex text-amber-500" data-id="element-725">
                      <span data-id="element-726">★★★★★</span>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 italic" data-id="element-727">
                  "Me encanta recibir gente en casa, pero siempre repetía lo
                  mismo. Ahora tengo ideas infinitas. Los dips y el calendario
                  son mis favoritos. Todos mis invitados sacan fotos antes de
                  comer."
                </p>
              </div>
              {/* Testimonio 5 */}
              <div className="bg-white rounded-lg p-6 border border-amber-100 shadow-sm" data-id="element-728">
                <div className="flex items-center mb-3" data-id="element-729">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mr-3" data-id="element-730">
                    <span className="text-amber-600 text-lg" data-id="element-731">H</span>
                  </div>
                  <div data-id="element-732">
                    <h3 className="font-bold text-amber-800" data-id="element-733">
                      Hernán, 45 años
                    </h3>
                    <p className="text-gray-600 text-sm" data-id="element-734">Mar del Plata</p>
                  </div>
                  <div className="ml-auto" data-id="element-735">
                    <div className="flex text-amber-500" data-id="element-736">
                      <span data-id="element-737">★★★★★</span>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 italic" data-id="element-738">
                  "Siempre pensaba que hacer una buena picada era caro. Este
                  sistema me enseñó a usar productos simples y que se vean
                  premium. Terminé gastando menos de la mitad de lo que gastaba
                  antes."
                </p>
              </div>
              {/* Testimonio 6 */}
              <div className="bg-white rounded-lg p-6 border border-amber-100 shadow-sm" data-id="element-739">
                <div className="flex items-center mb-3" data-id="element-740">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mr-3" data-id="element-741">
                    <span className="text-amber-600 text-lg" data-id="element-742">S</span>
                  </div>
                  <div data-id="element-743">
                    <h3 className="font-bold text-amber-800" data-id="element-744">Sofía, 26 años</h3>
                    <p className="text-gray-600 text-sm" data-id="element-745">La Plata</p>
                  </div>
                  <div className="ml-auto" data-id="element-746">
                    <div className="flex text-amber-500" data-id="element-747">
                      <span data-id="element-748">★★★★★</span>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 italic" data-id="element-749">
                  "Trabajo muchas horas y no tengo tiempo para cocinar. Con
                  Picadas Fáciles preparo algo rápido, rico y lindo para
                  compartir con amigos después del trabajo. Es un alivio tenerlo
                  todo organizado."
                </p>
              </div>
            </div>
          </div>
          {/* New "Oportunidad de Negocio" section */}
          <div className="bg-amber-50 rounded-xl p-8 border border-amber-100 mb-12" data-id="element-750">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-amber-800 mb-4" data-id="element-751">
              <BriefcaseEmoji size={28} className="inline-block mr-2" data-id="element-752" />{' '}
              Oportunidad de Negocio Real
            </h2>
            <p className="text-center text-amber-700 font-medium mb-8" data-id="element-753">
              Historias de éxito de quienes aplicaron Picadas Fáciles™
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-754">
              {/* Stat 1 */}
              <div className="bg-white rounded-lg p-6 border border-amber-100 shadow-sm text-center" data-id="element-755">
                <div className="flex justify-center mb-4" data-id="element-756">
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center" data-id="element-757">
                    <MoneyBagEmoji size={32} data-id="element-758" />
                  </div>
                </div>
                <div className="text-amber-700 font-bold text-xl mb-2" data-id="element-759">
                  AR$400.000+
                </div>
                <p className="text-gray-600" data-id="element-760">
                  Ingresos promedio de alumnas que ya venden picadas desde casa.
                </p>
              </div>
              {/* Stat 2 */}
              <div className="bg-white rounded-lg p-6 border border-amber-100 shadow-sm text-center" data-id="element-761">
                <div className="flex justify-center mb-4" data-id="element-762">
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center" data-id="element-763">
                    <LightningEmoji size={32} data-id="element-764" />
                  </div>
                </div>
                <div className="text-amber-700 font-bold text-xl mb-2" data-id="element-765">
                  Retorno inmediato
                </div>
                <p className="text-gray-600" data-id="element-766">
                  Con solo 1 o 2 picadas vendidas, recuperás lo que invertiste
                  en el sistema.
                </p>
              </div>
              {/* Stat 3 */}
              <div className="bg-white rounded-lg p-6 border border-amber-100 shadow-sm text-center" data-id="element-767">
                <div className="flex justify-center mb-4" data-id="element-768">
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center" data-id="element-769">
                    <BoxEmoji size={32} data-id="element-770" />
                  </div>
                </div>
                <div className="text-amber-700 font-bold text-xl mb-2" data-id="element-771">
                  20+ picadas al mes
                </div>
                <p className="text-gray-600" data-id="element-772">
                  Producción promedio de nuestros emprendedores, con clientes
                  que repiten y recomiendan.
                </p>
              </div>
            </div>
            <div className="bg-white rounded-lg p-6 border border-amber-100 shadow-sm mb-8" data-id="element-773">
              <p className="text-gray-700 mb-4" data-id="element-774">
                <span className="text-amber-600 font-medium" data-id="element-775">
                  <SparklesEmoji size={20} className="inline-block mr-1" data-id="element-776" />
                </span>{' '}
                Lo mejor: no necesitás experiencia previa, ni equipamiento
                especial. Solo seguís el método, aplicás las técnicas… y tus
                picadas se venden solas.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-777">
                <span className="text-amber-600 font-medium" data-id="element-778">
                  <RocketEmoji size={20} className="inline-block mr-1" data-id="element-779" />
                </span>{' '}
                Empezá hoy mismo tu negocio de picadas premium desde casa.
                Porque lo que ahora hacés para tus amigos… mañana puede pagarte
                las cuentas.
              </p>
            </div>
            <div className="flex justify-center" data-id="element-780">
              <a href="#" className="checkout-fresh bg-amber-600 hover:bg-amber-700 text-white font-bold py-4 px-8 rounded-lg shadow-lg transition-all transform hover:scale-105" onClick={handleCheckout} data-id="element-781">
                👉 QUIERO VENDER PICADAS YA - AR$17.990
              </a>
            </div>
          </div>
          {/* New Carousel Section - Replaced with static image */}
          <div id="recetas-exclusivas" className="bg-amber-50 rounded-xl p-8 border border-amber-100 mb-12" data-id="element-782">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-amber-800 mb-4" data-id="element-783">
              📚 Recetas Exclusivas de Bonificación
            </h2>
            <p className="text-center text-amber-700 font-medium mb-8" data-id="element-784">
              ¡Incluidas GRATIS con tu compra hoy!
            </p>
            {/* Static image replacing carousel */}
            <div className="flex justify-center" data-id="element-785">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/9M1B28ScVFf2TUFWWwiWeE/Generated_Image_September_13%2C_2025_-_7_39PM.png" alt="Picadas Fáciles - Colección Completa" className="w-auto h-auto rounded-lg mx-auto" style={{
              maxHeight: '400px',
              objectFit: 'contain'
            }} data-id="element-786" />
            </div>
            {/* Download button */}
            <div className="mt-8 flex justify-center" data-id="element-787">
              <a href="#" className="checkout-fresh bg-amber-600 hover:bg-amber-700 text-white font-bold py-4 px-8 rounded-lg shadow-lg transition-all transform hover:scale-105 flex items-center justify-center gap-3" onClick={handleCheckout} data-id="element-788">
                <DownloadIcon size={24} data-id="element-789" />
                <span className="text-lg uppercase" data-id="element-790">DESCARGAR - AR$17.990</span>
              </a>
            </div>
          </div>
          {/* New "Bonos Gratis Hoy" section */}
          <div className="bg-amber-50 rounded-xl p-8 border border-amber-100 mb-0" data-id="element-791">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-amber-800 mb-4" data-id="element-792">
              <GiftEmoji size={28} className="inline-block mr-2" data-id="element-793" /> ¡BONOS
              GRATIS HOY!
            </h2>
            <p className="text-center text-amber-700 font-medium mb-4" data-id="element-794">
              6 Recursos Exclusivos de Bonificación
            </p>
            <p className="text-center text-gray-700 mb-8" data-id="element-795">
              Valor adicional de AR$150.000 – ¡Incluido GRATIS solo por hoy!
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-796">
              {/* Bonus 1 */}
              <div className="bg-amber-50 rounded-lg p-6 shadow-sm relative overflow-hidden" data-id="element-797">
                <div className="absolute top-3 right-3" data-id="element-798">
                  <div className="bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full" data-id="element-799">
                    GRATIS
                  </div>
                </div>
                <div className="flex flex-col items-start gap-3" data-id="element-800">
                  <div className="w-full h-48 flex-shrink-0 rounded-md overflow-hidden bg-white shadow-sm" data-id="element-801">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/4yTMhFkt9FXxTaqyB83AED/bono_1_PARA_PAGINA.png" alt="Checklist de Compras Inteligente" className="w-full h-full object-cover" data-id="element-802" />
                  </div>
                  <div className="pt-2" data-id="element-803">
                    <h3 className="font-bold text-amber-800 mb-1 text-lg" data-id="element-804">
                      Checklist de Compras Inteligente
                    </h3>
                    <p className="text-gray-600 mb-3" data-id="element-805">
                      Cantidades exactas por persona · Alternativas económicas ·
                      Combos de súper para gastar menos
                    </p>
                    <p className="text-amber-600 font-medium" data-id="element-806">
                      Valor: AR$25.000
                    </p>
                    <span className="bg-green-600 px-2 py-0.5 rounded-full text-white" data-id="element-807">
                      GRATIS HOY
                    </span>
                  </div>
                </div>
              </div>
              {/* Bonus 2 */}
              <div className="bg-amber-50 rounded-lg p-6 shadow-sm relative overflow-hidden" data-id="element-808">
                <div className="absolute top-3 right-3" data-id="element-809">
                  <div className="bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full" data-id="element-810">
                    GRATIS
                  </div>
                </div>
                <div className="flex flex-col items-start gap-3" data-id="element-811">
                  <div className="w-full h-48 flex-shrink-0 rounded-md overflow-hidden bg-white shadow-sm" data-id="element-812">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/1bUxhXrrBBnPnx1w2UtQwb/bono_2_PARA_PAGINA.png" alt="Recetario Premium de Dips & Untables" className="w-full h-full object-cover" data-id="element-813" />
                  </div>
                  <div className="pt-2" data-id="element-814">
                    <h3 className="font-bold text-amber-800 mb-1 text-lg" data-id="element-815">
                      Recetario Premium de Dips & Untables
                    </h3>
                    <p className="text-gray-600 mb-3" data-id="element-816">
                      10 recetas fáciles, desde hummus clásico hasta mayonesa
                      casera y dips gourmet.
                    </p>
                    <p className="text-amber-600 font-medium" data-id="element-817">
                      Valor: AR$25.000
                    </p>
                    <span className="bg-green-600 px-2 py-0.5 rounded-full text-white" data-id="element-818">
                      GRATIS HOY
                    </span>
                  </div>
                </div>
              </div>
              {/* Bonus 3 */}
              <div className="bg-amber-50 rounded-lg p-6 shadow-sm relative overflow-hidden" data-id="element-819">
                <div className="absolute top-3 right-3" data-id="element-820">
                  <div className="bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full" data-id="element-821">
                    GRATIS
                  </div>
                </div>
                <div className="flex flex-col items-start gap-3" data-id="element-822">
                  <div className="w-full h-48 flex-shrink-0 rounded-md overflow-hidden bg-white shadow-sm" data-id="element-823">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/nCT5uPywH3rVhZJYtDAKft/bono_3_PARA_PAGINA.png" alt="Calendario de Picadas (30 días)" className="w-full h-full object-cover" data-id="element-824" />
                  </div>
                  <div className="pt-2" data-id="element-825">
                    <h3 className="font-bold text-amber-800 mb-1 text-lg" data-id="element-826">
                      Calendario de Picadas (30 días)
                    </h3>
                    <p className="text-gray-600 mb-3" data-id="element-827">
                      Un mes entero de ideas variadas: económicas, rápidas,
                      gourmet y familiares.
                    </p>
                    <p className="text-amber-600 font-medium" data-id="element-828">
                      Valor: AR$25.000
                    </p>
                    <span className="bg-green-600 px-2 py-0.5 rounded-full text-white" data-id="element-829">
                      GRATIS HOY
                    </span>
                  </div>
                </div>
              </div>
              {/* Bonus 4 */}
              <div className="bg-amber-50 rounded-lg p-6 shadow-sm relative overflow-hidden" data-id="element-830">
                <div className="absolute top-3 right-3" data-id="element-831">
                  <div className="bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full" data-id="element-832">
                    GRATIS
                  </div>
                </div>
                <div className="flex flex-col items-start gap-3" data-id="element-833">
                  <div className="w-full h-48 flex-shrink-0 rounded-md overflow-hidden bg-white shadow-sm" data-id="element-834">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/tis8dHvJAEKFqm7panRwxB/bono_4_PARA_PAGINA.png" alt="Tablas Visuales + Guía de Presentación Premium" className="w-full h-full object-cover" data-id="element-835" />
                  </div>
                  <div className="pt-2" data-id="element-836">
                    <h3 className="font-bold text-amber-800 mb-1 text-lg" data-id="element-837">
                      Tablas Visuales + Guía de Presentación Premium
                    </h3>
                    <p className="text-gray-600 mb-3" data-id="element-838">
                      12 tableros visuales, plantillas de armado y técnicas de
                      presentación fotogénica.
                    </p>
                    <p className="text-amber-600 font-medium" data-id="element-839">
                      Valor: AR$25.000
                    </p>
                    <span className="bg-green-600 px-2 py-0.5 rounded-full text-white" data-id="element-840">
                      GRATIS HOY
                    </span>
                  </div>
                </div>
              </div>
              {/* Bonus 5 */}
              <div className="bg-amber-50 rounded-lg p-6 shadow-sm relative overflow-hidden" data-id="element-841">
                <div className="absolute top-3 right-3" data-id="element-842">
                  <div className="bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full" data-id="element-843">
                    GRATIS
                  </div>
                </div>
                <div className="flex flex-col items-start gap-3" data-id="element-844">
                  <div className="w-full h-48 flex-shrink-0 rounded-md overflow-hidden bg-white shadow-sm" data-id="element-845">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/rECzGfg94kLoZ91zyzHeXh/bono_5_PARA_PAGINA.png" alt="Mini Curso Express: Cómo Vender Picadas Desde Casa" className="w-full h-full object-cover" data-id="element-846" />
                  </div>
                  <div className="pt-2" data-id="element-847">
                    <h3 className="font-bold text-amber-800 mb-1 text-lg" data-id="element-848">
                      Mini Curso Express: Cómo Vender Picadas Desde Casa
                    </h3>
                    <p className="text-gray-600 mb-3" data-id="element-849">
                      Estrategias de precios, menú digital y ventas por WhatsApp
                      e Instagram.
                    </p>
                    <p className="text-amber-600 font-medium" data-id="element-850">
                      Valor: AR$25.000
                    </p>
                    <span className="bg-green-600 px-2 py-0.5 rounded-full text-white" data-id="element-851">
                      GRATIS HOY
                    </span>
                  </div>
                </div>
              </div>
              {/* Bonus 6 */}
              <div className="bg-amber-50 rounded-lg p-6 shadow-sm relative overflow-hidden" data-id="element-852">
                <div className="absolute top-3 right-3" data-id="element-853">
                  <div className="bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full" data-id="element-854">
                    GRATIS
                  </div>
                </div>
                <div className="flex flex-col items-start gap-3" data-id="element-855">
                  <div className="w-full h-48 flex-shrink-0 rounded-md overflow-hidden bg-white shadow-sm" data-id="element-856">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/6JXUaUnPTTjSP5VmngV5VF/bono_6_PARA_PAGINA.png" alt="Combo de Picadas Temáticas + Playlist y Bebidas" className="w-full h-full object-cover" data-id="element-857" />
                  </div>
                  <div className="pt-2" data-id="element-858">
                    <h3 className="font-bold text-amber-800 mb-1 text-lg" data-id="element-859">
                      Combo de Picadas Temáticas + Playlist y Bebidas
                    </h3>
                    <p className="text-gray-600 mb-3" data-id="element-860">
                      10 combos completos con playlists en Spotify y maridajes
                      accesibles.
                    </p>
                    <p className="text-amber-600 font-medium" data-id="element-861">
                      Valor: AR$25.000
                    </p>
                    <span className="bg-green-600 px-2 py-0.5 rounded-full text-white" data-id="element-862">
                      GRATIS HOY
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg p-6 border border-amber-100 shadow-sm mb-8" data-id="element-863">
              <p className="text-gray-700 text-center mb-0" data-id="element-864">
                <span className="text-amber-600 font-medium" data-id="element-865">✨</span> Solo por
                hoy, obtendrás estos 6 recursos premium completamente GRATIS. Un
                valor adicional de AR$150.000 incluido sin costo extra.
              </p>
            </div>
            <div className="flex justify-center" data-id="element-866">
              <a href="#" className="checkout-fresh bg-amber-600 hover:bg-amber-700 text-white font-bold py-4 px-8 rounded-lg shadow-lg transition-all transform hover:scale-105" onClick={handleCheckout} data-id="element-867">
                👉 COMPRAR AHORA - AR$17.990
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>;
}